import { useEffect, useState } from "react";
import { Fingerprint, Lock, ScanFace } from "lucide-react";
import { toast } from "sonner";
import { biometrics, hasBiometricBridge } from "@/lib/nativeBridge";
import { Button } from "@/components/ui/button";

const methodLabel = (method) =>
  method === "face" ? "reconhecimento facial" : method === "fingerprint" ? "impressão digital" : "biometria do aparelho";

/**
 * Bloqueio por biometria. Só aparece dentro dos aplicativos Erimus, onde a casca nativa
 * expõe a ponte com o leitor de digital ou o reconhecimento facial do aparelho.
 */
export const BiometricLockCard = ({ productName }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!hasBiometricBridge()) return;
    biometrics.status().then((result) => setStatus(result || { supported: false }));
  }, []);

  if (!hasBiometricBridge() || !status) return null;

  const Icon = status.method === "face" ? ScanFace : status.method === "fingerprint" ? Fingerprint : Lock;
  const unavailable = !status.supported || !status.enrolled;

  const toggle = async () => {
    setBusy(true);
    const result = await biometrics.set(!status.enabled);
    setBusy(false);
    if (!result) {
      toast.error("Não foi possível falar com o aplicativo. Tente novamente.");
      return;
    }
    setStatus({ ...status, ...result });
    if (result.ok) {
      toast.success(result.enabled ? "Bloqueio por biometria ativado" : "Bloqueio por biometria desativado");
    } else if (result.reason === "unavailable") {
      toast.error("Cadastre uma digital ou o reconhecimento facial nos ajustes do aparelho.");
    } else {
      toast.error("Não foi possível confirmar sua identidade.");
    }
  };

  return (
    <section data-testid="biometric-lock-card" className="er-card">
      <p className="er-label">Segurança do aplicativo</p>
      <div className="mt-3 flex items-start gap-3">
        <Icon size={18} className="mt-0.5 shrink-0 text-sage-600" aria-hidden="true" />
        <div>
          <p className="font-display text-base font-medium text-ink-900">Bloqueio por biometria</p>
          <p className="mt-1 text-sm leading-relaxed text-ink-500">
            {unavailable
              ? "Este aparelho ainda não tem digital ou reconhecimento facial cadastrado. Configure nos ajustes do sistema para usar o bloqueio."
              : `Pedir ${methodLabel(status.method)} para abrir o ${productName}, inclusive ao voltar de outro aplicativo.`}
          </p>
          <p data-testid="biometric-lock-state" className="mt-2 text-xs font-semibold text-sage-700">
            {status.enabled ? "Ativado" : "Desativado"}
          </p>
        </div>
      </div>
      <Button
        data-testid="biometric-lock-toggle"
        onClick={toggle}
        disabled={busy || unavailable}
        variant="outline"
        className={`mt-5 h-11 rounded-full ${
          status.enabled
            ? "border-sage-600/40 bg-white text-sage-700 hover:bg-sage-50"
            : "border-transparent bg-sage-600 text-white hover:bg-sage-700"
        }`}
      >
        {busy ? "Aguardando confirmação…" : status.enabled ? "Desativar bloqueio" : "Ativar bloqueio"}
      </Button>
    </section>
  );
};
